// packages/front/src/_common/lib/format-currency.ts
//
// Currency formatting utilities.
// formatCurrency uses the DISPLAY_CURRENCY_SYMBOL map from shared constants
// to render amounts with the correct currency symbol.

import { DISPLAY_CURRENCY, DISPLAY_CURRENCY_SYMBOL } from "@signals/shared";

/**
 * Format a number as a currency amount with the appropriate symbol.
 * @param v - The amount to format
 * @param currency - ISO currency code (USD, RUB, EUR, CNH)
 * @returns Formatted string like "$1,234.56" or "₽1,234.56"
 */
export function formatCurrency(
  v: number | undefined | null,
  currency: string = "USD",
): string {
  if (v === undefined || v === null || Number.isNaN(v)) return "—";
  const displayCurrency = DISPLAY_CURRENCY.find(
    (supportedCurrency) => supportedCurrency === currency,
  );
  const symbol = displayCurrency
    ? DISPLAY_CURRENCY_SYMBOL[displayCurrency]
    : "";
  const formatted = formatNumber(v);
  return formatted.startsWith("-")
    ? `-${symbol}${formatted.slice(1)}`
    : `${symbol}${formatted}`;
}

/**
 * Format a number as a plain number (no currency symbol).
 */
export function formatNumber(v: number | undefined | null): string {
  if (v === undefined || v === null || Number.isNaN(v)) return "—";
  return v.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    signDisplay: "negative",
  });
}

/**
 * Format a percentage value.
 */
export function formatPercent(v: number | undefined | null): string {
  if (v === undefined || v === null || Number.isNaN(v)) return "—";
  return `${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2, signDisplay: "negative" })}%`;
}
